// libraries
import { Spinner } from '@interstate/components/Spinner';
import { ChevronDownIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';

// hooks
import { useAiAltVehicleOptions } from './useAiAltVehicleOptions';

// components
import AiAltVehicleOptionsCard from './AiAltVehicleOptionsCard';

// styles
import {
    AiAltVehicleOptionsSectionContainer,
    AiAltVehicleOptionsSectionBanner,
    AiAltVehicleOptionsSectionHeader,
    AiAltVehicleOptionsHeaderTitleGroup,
    AiAltVehicleOptionsHeaderTitle,
    AiAltVehicleOptionsSectionSubtitle,
    AiAltVehicleOptionsChevron,
    AiAltVehicleOptionsCollapsible,
    AiAltVehicleOptionsCardRow
} from './AiAltVehicleOptionsSection.style';
import { SearchSpinnerContainer } from './SearchAndSelectVehicle.style';

/**
 * Alternative Vehicle Options section on the Change Vehicle screen (F291824). Rendered once by
 * `AiAssistedSearch`, above the search results and outside the results subtree so it survives the
 * spinner / results / no-results transitions. The caller gates visibility on
 * `isAiAltVehicleOptionsSectionVisible` (feature toggles + a finance/lease-eligible deal), so this
 * component does not re-check the gate. Data, state, and the fire-once fetch live in
 * `useAiAltVehicleOptions`; this component is a thin renderer over that hook.
 * Render states: loading → bare spinner; no recommendations → null (AC-7.1); results → AI-badged,
 * collapsible header + up to 3 recommendation cards.
 */
const AiAltVehicleOptionsSection = () => {
    const theme = useInterstateTheme();
    const { cards, isLoading, isExpanded, toggleExpanded, selectVehicle, isSelected } = useAiAltVehicleOptions();

    if (isLoading) {
        return (
            <SearchSpinnerContainer>
                <Spinner data-testid="ai-alt-vehicle-options-loading-spinner" />
            </SearchSpinnerContainer>
        );
    }

    if (cards.length === 0) {
        return null;
    }

    return (
        <AiAltVehicleOptionsSectionContainer data-testid="ai-alt-vehicle-options-section">
            <AiAltVehicleOptionsSectionBanner
                type="button"
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                data-testid="ai-alt-vehicle-options-section-toggle"
            >
                <AiAltVehicleOptionsSectionHeader>
                    <AiAltVehicleOptionsHeaderTitleGroup>
                        <AiAltVehicleOptionsChevron isExpanded={isExpanded}>
                            <ChevronDownIcon color={theme.tokens.SemColorOnSurfaceInverse} fontSize="16px" />
                        </AiAltVehicleOptionsChevron>
                        <AiAltVehicleOptionsHeaderTitle variant="anchor-block-md" tag="span">
                            Alternative Vehicle Options
                        </AiAltVehicleOptionsHeaderTitle>
                        <AiLogoIcon data-testid="ai-alt-vehicle-options-ai" fontSize="24px" ariaHidden={true} />
                    </AiAltVehicleOptionsHeaderTitleGroup>
                </AiAltVehicleOptionsSectionHeader>
                <AiAltVehicleOptionsSectionSubtitle variant="body-md" tag="span">
                    Explore more affordable vehicles in our inventory with better payment options.
                </AiAltVehicleOptionsSectionSubtitle>
            </AiAltVehicleOptionsSectionBanner>
            <AiAltVehicleOptionsCollapsible isExpanded={isExpanded} data-testid="ai-alt-vehicle-options-collapsible">
                <div>
                    <AiAltVehicleOptionsCardRow data-testid="ai-alt-vehicle-options-card-row">
                        {cards.map((recommendation) => (
                            <AiAltVehicleOptionsCard
                                key={recommendation.vehicle.vin}
                                recommendation={recommendation}
                                isSelected={isSelected(recommendation)}
                                onSelect={() => selectVehicle(recommendation)}
                            />
                        ))}
                    </AiAltVehicleOptionsCardRow>
                </div>
            </AiAltVehicleOptionsCollapsible>
        </AiAltVehicleOptionsSectionContainer>
    );
};

export default AiAltVehicleOptionsSection;
